/**
 * A sport's own definition, on one page.
 *
 * This used to be "fixture setup" — the half of a sport that onboarding did not
 * cover, split off because it made the old definition screen unreadable. There
 * is no half any more. The whole sport is here.
 */

import { useParams } from "react-router-dom";

import { Crumbs, PageHead, Problem } from "@/components/kit";
import { SportWizard } from "@/components/sport/SportWizard";
import { getDefinition, getDraft, listDefinitions } from "@/lib/api/client";
import { displayName } from "@/lib/format";
import { useApi } from "@/spa/useApi";

export function SportSetupPage() {
  const { sport = "" } = useParams<{ sport: string }>();

  const { data, loading, error } = useApi(
    async () => {
      const definitions = await listDefinitions();
      if (!definitions.some((d) => d.sportCode === sport)) return { missing: true as const };
      const codes = [...new Set(definitions.map((d) => d.sportCode))];
      const published = definitions.some(
        (d) => d.sportCode === sport && d.status === "PUBLISHED",
      );
      // The draft is what setup edits; a sport with no draft falls back to its
      // published document, and one with neither has nothing to show.
      const doc = await getDraft(sport).catch(() =>
        getDefinition(sport).catch(() => undefined),
      );
      if (!doc) return { missing: true as const };
      return { missing: false as const, doc, codes, published };
    },
    [sport],
    "failed to load",
  );

  if (error) {
    return (
      <Problem>
        Could not load {sport}: {error}
      </Problem>
    );
  }
  if (!data) return <PageHead title="Setup" note={loading ? "Loading…" : ""} />;
  if (data.missing) return <Problem>No sport called {sport}.</Problem>;

  return (
    <>
      <Crumbs
        trail={[
          { label: "Sports", href: "/sports" },
          { label: displayName(sport), href: `/${sport}` },
          { label: "Setup" },
        ]}
      />
      <SportWizard
        initial={data.doc}
        published={data.published}
        sports={data.codes.map((code) => ({ code, label: displayName(code) }))}
      />
    </>
  );
}
